import { Message, MessageBox } from 'element-ui';
import { printList } from './config.js';
import { dispatchPrint, outboundPrint, naixuePrint, protocolPrint, bluemoonPrint, updatePrintCount, checkPickupPrint } from '@/modules/transport/otp/api/performancePrint.js';

// 当前组件上下文
let componentContext = null;

/**
 * @description 字节流转为pdf打开
 * @param {Object} stream 字节流
 */
const toPdfOpen = stream => {
    const blob = new Blob([stream], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
};

// 获取当前打印单据配置项
const getPrintConfigData = () => {
    return printList.find(v => v.value === componentContext.printRadio);
};

/**
 * @description 更新打印次数
 * @param {Object} params 更新打印接口参数
 * @param {String} url 打印的链接
 */
const handleUpdatePrintCount = (params, url) => {
    if (componentContext.repeatPrintReason && Object.keys(componentContext.repeatPrintReason).length) {
        params.reason = componentContext.repeatPrintReason.reason || '';
        params.remark = componentContext.repeatPrintReason.remark || '';
    }
    updatePrintCount(params).then(res => {
        if (res && +res.code === 0) {
            url && window.open(url) && componentContext.beforeCloseAllDialog();
        }
    }).catch(() => { });
};

/**
 * @description 打印确认弹窗。当打印次数大于0时需要弹窗提示是否继续打印
 * @param {Number} printCount 打印次数
 * @param {String} url 打印的链接
 */
const printConfirm = (printCount, url, params) => {
    const printObj = getPrintConfigData();
    MessageBox.confirm(`该${printObj.label}已打印${printCount}次，是否继续？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    })
        .then(() => {
            handleUpdatePrintCount(params, url);
        })
        .catch(() => { });
};

/**
 * @description 是否弹窗提示确认再次打印。如果打印次数大于0，需要弹窗提示是否确认再次打印
 * @param {Number} printCount 打印次数
 * @param {String} url 打印的链接
 * @param {Object} params 打印接口需要的参数
 */
const isConfirmUpdatePrintCount = (printCount, url, params) => {
    printCount > 0 ? printConfirm(printCount, url, params) : handleUpdatePrintCount(params, url);
};

/**
 * @description 打印参数
 */
const setPrintParams = () => {
    return {
        collectNo: '', // 集配号
        orderNo: componentContext.componentsData.dispatchNo, // 订单号，取发车单号
        templateCode: componentContext.printRadio // 打印模板编码
    };
};

// 检查后端是否返回checkMsg字段消息
const checkMsgIsExist = (data, params) => {
    if (data.checkMsg) {
        MessageBox.confirm(data.checkMsg, '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        })
            .then(() => {
                isConfirmUpdatePrintCount(data.printCount, data.url, params);
            })
            .catch(() => { });
    } else {
        isConfirmUpdatePrintCount(data.printCount, data.url, params);
    }
};

/**
 * @description 发车单打印。url链接打印。即根据后端返回url链接跳转到打印页面
 */
const handleDispatchPrint = () => {
    componentContext.btnLoading = true;
    const params = setPrintParams();
    dispatchPrint(params)
        .then(res => {
            if (res && +res.code === 0) {
                checkMsgIsExist(res.data, params);
            }
        })
        .finally(() => {
            componentContext.btnLoading = false;
        });
};

/**
 * @description 出库单打印
 */
const handleOutboundPrint = () => {
    componentContext.btnLoading = true;
    outboundPrint({
        orderNo: componentContext.componentsData.dispatchNo
    })
        .then(res => {
            if (res && +res.code === 0) {
                handleUpdatePrintCount(setPrintParams(), res.data.url);
            }
        })
        .finally(() => {
            componentContext.btnLoading = false;
        });
};

/**
 * @description 配送单-奈雪/奈雪2B打印
 */
const handleNaixuePrint = () => {
    componentContext.btnLoading = true;
    naixuePrint(setPrintParams())
        .then(res => {
            handleUpdatePrintCount(setPrintParams());
            toPdfOpen(res);
            componentContext.beforeCloseAllDialog();
        })
        .finally(() => {
            componentContext.btnLoading = false;
        });
};

/**
 * @description 运输协议打印
 */
const handleTransportPrint = () => {
    componentContext.btnLoading = true;
    protocolPrint({
        orderNo: componentContext.componentsData.dispatchNo, // 订单号，取发车单号
        templateCode: componentContext.printRadio // 打印模板编码
    })
        .then(res => {
            handleUpdatePrintCount(setPrintParams());
            toPdfOpen(res);
            componentContext.beforeCloseAllDialog();
        })
        .finally(() => {
            componentContext.btnLoading = false;
        });
};

/**
 * @description 蓝月亮打印
 */
const handleBluemoonPrint = () => {
    componentContext.btnLoading = true;
    bluemoonPrint({
        dispatchNo: componentContext.componentsData.dispatchNo
    })
        .then(res => {
            if (res && +res.code === 0 && res.data.success === 'Y') {
                const resultList = res.data.resultList || [];
                if (resultList.length === 0) {
                    componentContext.$message.warning('暂无单据可打印！');
                } else {
                    handleUpdatePrintCount(setPrintParams());
                }
                if (resultList.length > 1) {
                    componentContext.bluemoonList = resultList;
                    componentContext.$nextTick(() => {
                        componentContext.bluemoonPrintVisible = true;
                    });
                }
                if (resultList.length === 1) {
                    window.open(resultList[0].url);
                    componentContext.beforeCloseAllDialog();
                }
            }
        })
        .finally(() => {
            componentContext.btnLoading = false;
        });
};

/**
 * @description 处理打印逻辑
 * @param {Object} ctx 当前组件上下文
 */
export const dealWithPrintLogic = ctx => {
    componentContext = ctx; // 保存当前组件上下文
    const target = getPrintConfigData();
    /* eslint-disable indent */
    switch (target.printType) {
        case 'urlPrint':
            handleDispatchPrint(); // 普通打印
            break;
        case 'ckdPrint':
            handleOutboundPrint(); // 出库单打印
            break;
        case 'nxPrint':
            handleNaixuePrint(); // 奈雪打印
            break;
        case 'transportPrint':
            handleTransportPrint(); // 运输协议打印
            break;
        case 'bluemoonPrint':
            handleBluemoonPrint(); // 蓝月亮打印
            break;
        default:
            break;
    }
};

/**
 * @description 打印校验
 */
export const isValidate = async (printRadio, componentsData) => {
    if (!printRadio) {
        Message.warning('请选择要打印的单据！');
        return false;
    }
    if (printRadio === 'tms_pickup_trunk') {
        // 发车单下的运输单若没有装货仓库则不允许打印提货单
        const isNoWhName = await validatePickupPrint(componentsData.dispatchNo);
        return isNoWhName;
    }
    return true;
};

// 打印提货单校验
const validatePickupPrint = (dispatchNo) => {
    return checkPickupPrint({ dispatchNo }).then(res => {
        if (res && +res.code === 0) {
            if (!res.data) {
                return MessageBox.confirm('该订单没有装货仓库（请进行分仓操作），是否继续打印提货单？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                }).then(() => {
                    return true; // 用户点击了 "确认" 按钮
                }).catch(() => {
                    return false; // 用户点击了 "取消" 按钮
                });
            }
            return true;
        }
    }).catch(() => {
        return false;
    });
};
